import React, { useMemo, useState } from 'react';
import { DonutChart, DonutSegment } from '../donut-chart/donut-chart';
import { Tile } from '../tile';
import { chartTokens } from '../charts/chart-tokens';
import { formatCompact } from '../charts/chart-utils';
import './automation-results-chart.scss';

export interface AutomationResultsBreakdown {
  autoHigh?: number;
  autoRule?: number;
  manualLow?: number;
  manualRule?: number;
  manualIneligible?: number;
  manualNotValid?: number;
  manualOther?: number;
  uncategorized?: number;
}

export interface AutomationResultsChartProps {
  breakdown: AutomationResultsBreakdown;
  totalCasesLabel?: string;
  size?: number;
  hideLegend?: boolean;
  ariaLabel?: string;
}

interface LegendItem {
  key: keyof AutomationResultsBreakdown;
  label: string;
  color: string;
}

const LEGEND: LegendItem[] = [
  { key: 'autoHigh',         label: 'Auto: High',         color: chartTokens.legendDot.autoHigh },
  { key: 'autoRule',         label: 'Auto: Rule',         color: chartTokens.legendDot.autoRule },
  { key: 'manualLow',        label: 'Manual: Low',        color: chartTokens.legendDot.manualLow },
  { key: 'manualRule',       label: 'Manual: Rule',       color: chartTokens.legendDot.manualRule },
  { key: 'manualIneligible', label: 'Manual: Ineligible', color: chartTokens.legendDot.manualIneligible },
  { key: 'manualNotValid',   label: 'Manual: Not Valid',  color: chartTokens.legendDot.manualNotValid },
  { key: 'manualOther',      label: 'Manual: Other',      color: chartTokens.legendDot.manualOther },
  { key: 'uncategorized',    label: 'Uncategorized',      color: chartTokens.legendDot.uncategorized },
];

/**
 * Buckets the payload does not carry, and buckets that carry no cases, are
 * omitted rather than drawn as zero-width slices.
 */
const presentEntries = (b: AutomationResultsBreakdown): LegendItem[] =>
  LEGEND.filter((entry) => (b[entry.key] ?? 0) > 0);

const segmentsFromBreakdown = (b: AutomationResultsBreakdown): DonutSegment[] =>
  presentEntries(b).map((entry) => ({
    key: entry.key,
    label: entry.label,
    value: b[entry.key] ?? 0,
    color: entry.color,
  }));

const sumBreakdown = (b: AutomationResultsBreakdown): number =>
  LEGEND.reduce((acc, entry) => acc + (b[entry.key] ?? 0), 0);

export const AutomationResultsChart: React.FC<AutomationResultsChartProps> = ({
  breakdown,
  totalCasesLabel,
  size = 180,
  hideLegend = false,
  ariaLabel = 'Automation results',
}) => {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);

  const total = useMemo(() => sumBreakdown(breakdown), [breakdown]);
  const segments = useMemo(() => segmentsFromBreakdown(breakdown), [breakdown]);
  const legendEntries = useMemo(() => presentEntries(breakdown), [breakdown]);

  const handleSegmentHover = (seg: DonutSegment) => setHoveredKey(seg.key);
  const handleSegmentLeave = () => setHoveredKey(null);

  return (
    <Tile
      title="Automation Results"
      subtitle="Hover segments for details"
      className="automation-results-chart-tile"
      ariaLabel={ariaLabel}
    >
      <section className="automation-results-chart" aria-label={ariaLabel}>
        <DonutChart
          data={segments}
          size={size}
          strokeWidth={28}
          strokeWidthHovered={32}
          centerLabel={{
            primary: totalCasesLabel ?? formatCompact(total),
            secondary: 'TOTAL CASES',
          }}
          ariaLabel={`${ariaLabel} donut`}
          onSegmentHover={handleSegmentHover}
          onSegmentLeave={handleSegmentLeave}
          hoveredKey={hoveredKey}
        />

        {hideLegend ? null : (
          <ul className="automation-results-chart__legend" aria-label="Legend">
            {legendEntries.map((entry) => {
              const value = breakdown[entry.key] ?? 0;
              const pct = total > 0 ? (value / total) * 100 : 0;
              const isHovered = hoveredKey === entry.key;
              return (
                <li
                  key={entry.key}
                  className={
                    isHovered
                      ? 'automation-results-chart__legend-row automation-results-chart__legend-row--hovered'
                      : 'automation-results-chart__legend-row'
                  }
                  onMouseEnter={() => setHoveredKey(entry.key)}
                  onMouseLeave={() => setHoveredKey(null)}>
                  <span
                    className="automation-results-chart__legend-dot"
                    style={{ background: entry.color }}
                    aria-hidden="true"
                  />
                  <span className="automation-results-chart__legend-label">
                    {entry.label}
                  </span>
                  <span className="automation-results-chart__legend-value">
                    {formatCompact(value)}
                  </span>
                  <span className="automation-results-chart__legend-pct">
                    {`${pct.toFixed(1)}%`}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </Tile>
  );
};

AutomationResultsChart.displayName = 'AutomationResultsChart';
